<template>
  <DrawerVue :refreshDisplay="refreshDisplay">
    <v-container fluid class="mt-4">
      <v-row class="mb-4" align="center">
        <v-col cols="auto">
          <h2 v-if="digitalMediaData">
            {{ $t("basics.edit", { type: $t("basics.digitalMedia") }) }}
          </h2>
          <h2 v-else>
            {{ $t("basics.new", { type: $t("basics.digitalMedia") }) }}
          </h2>
        </v-col>


        <v-spacer />

        <v-col cols="auto" v-if="digitalMediaData && $checkFunctionPermission(['digital_media_delete_detail'])">
          <v-tooltip :text="$t('basics.delete')">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" @click="showDeleteDigitalMedia()" class="icon-transparency hoverable">
                mdi-trash-can
              </v-icon>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-form ref="digitalmediaEditForm">

        <v-row>
          <v-col cols="12">
            <v-select :disabled="digitalMediaData != undefined" variant="outlined" flat :items="availTypes"
              :rules="[requiredRule]" v-model="type" :label="$t('basics.type') + '*'"
              @update:model-value="selectPresets" returnObject item-title="name" item-value="name"
              :readonly="readonly"></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field variant="outlined" flat :rules="[requiredRule, max256]" v-model="name"
              :label="$t('basics.name') + '*'" :readonly="readonly"></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea variant="outlined" flat :rules="[max5000]" v-model="description"
              :label="$t('basics.description')" :readonly="readonly"></v-textarea>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field variant="outlined" flat type="number" step="0.01" :rules="[requiredRule]" v-model="price"
              :label="$t('basics.price') + (isSubscription ? ' / Monat*' : '*')" :readonly="readonly"
              @blur="validatePrice"></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field variant="outlined" flat type="number" step="1" :rules="[requiredRule]" v-model="duration"
              :label="$t('basics.durationSimple') + ' (min)*'" :readonly="readonly"></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-select variant="outlined" flat :items="availStatuses" :rules="[requiredRule]" v-model="status"
              :label="$t('basics.status') + '*'" returnObject
              :item-title="(item) => $t('constants.product_status_' + item.name)" item-value="name"
              :readonly="readonly"></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-select variant="outlined" flat :items="availBundles" :rules="[]" v-model="bundles"
              :label="'Bundles'" multiple chips closable-chips item-title="name" item-value="id"
              :readonly="readonly"></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-combobox variant="outlined" flat :items="availTags" :rules="[]" v-model="tags" @update:search="fetchTags"
              autocomplete="off" :label="$t('basics.tags')" return-object chips multiple item-title="name"
              item-value="name" :readonly="readonly"></v-combobox>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-radio-group v-model="isSubscription" :readonly="readonly" color="primary" hide-details inline>
              <v-radio :label='$t("constants.one_time")' :value="false" />
              <v-radio :label='$t("constants.subscription")' :value="true" />
            </v-radio-group>

            <small v-if="isSubscription">
              {{$t("messages.subscriptionText")}}
            </small>
          </v-col>
        </v-row>

        <v-row v-if="!readonly">
          <v-col cols="12">
            <v-btn large block flat class="primaryButton" @click="confirm()" :disabled="loadingRelations || relationsFailed" ref="confirmButton" style="width: 100%">
              <span>{{ $t("basics.confirm") }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
export default {
  name: "digitalmediaEditDrawer",
  props: ["digitalMediaData", "refreshDisplay", "readonly"],
  emits: ["close", "delete"],

  data() {
    return {
      name: undefined,
      description: undefined,
      price: undefined,
      isSubscription: false,
      status: undefined,
      type: undefined,
      bundles: [],
      loadingRelations: false,
      relationsFailed: false,
      duration: undefined,

      availStatuses: [],
      availPaymentMethods: [],
      availTypes: [],
      availBundles: [],

      availTags: undefined,
      tags: undefined,

      requiredRule: (value) =>
        value !== null && value !== undefined && value !== '' ||
        this.$i18n.t("messages.required"),
      max256: (value) =>
        !value || value.length <= 256 || this.$i18n.t("messages.tooLong", { count: 256 }),
      max5: (value) =>
        !value || value.length <= 5 || this.$i18n.t("messages.tooLong", { count: 5 }),
      max5000: (value) =>
        !value ||
        value.length <= 5000 ||
        this.$i18n.t("messages.tooLong", { count: 5000 }),
    };
  },

  components: {
  },

  watch: {
    async refreshDisplay(value) {
      if (!value) return;
      this.loadingRelations = true;
      this.relationsFailed = false;
      try {
        await Promise.all([this.fetchStatuses(), this.fetchTypes(), this.fetchBundles(), this.fetchTags()]);
        this.setValues();
      } catch (error) {
        this.relationsFailed = true;
        window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: error }));
      } finally { this.loadingRelations = false; }
    },
  },

  methods: {
    validatePrice(val) {
      if (this.price > 99999) {
        this.price = 99999
      }
    },
    showDeleteDigitalMedia() {
      this.$emit("delete")
    },
    selectPresets(type) {
      if (type) {
        if (type.default_price !== undefined) {
          this.price = type.default_price
        }
      }
    },
    async fetchStatuses() {
      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "media/productstatuses/")
          .then((resp) => {
            this.availStatuses = resp.data

            resolve()
          })
          .catch((e) => {
            reject()
            console.log(e);
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
          });
      })
    },
    async fetchTypes() {
      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "media/producttypes/?size=10000")
          .then((resp) => {
            this.availTypes = resp.data.results

            resolve()
          })
          .catch((e) => {
            reject()
            console.log(e);
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
          });
      })
    },
    async fetchBundles() {
      const items = [];
      let url = this.axios.defaults.baseURL + "media/digitalbundles/";
      while (url) {
        const { data } = await this.axios.get(url);
        items.push(...(data.results ?? data));
        url = data.next || null;
      }
      this.availBundles = items;
    },
    async confirm() {
      if (this.readonly || this.loadingRelations || this.relationsFailed) return;
      const { valid } = await this.$refs.digitalmediaEditForm.validate();

      if (valid) {
        let tags = []
        for (let i = 0; i < (this.tags || []).length; i++) {
          if ((typeof this.tags[i]) == "string") {
            tags.push(this.tags[i])
          }
          else if (this.tags[i]) {
            tags.push(this.tags[i].name)
          }
        }

        let formData = {
          name: this.name,
          is_subscription: this.isSubscription,
          duration: this.duration,
          description: this.description,
          price: this.price !== undefined && this.price !== null
            ? parseFloat(this.price)
            : null,
          type: this.type.id,
          digital_bundles: this.bundles,
          tags: tags
        };

        if (this.status !== undefined) {
          formData.status = this.status.key
        }


        if (this.digitalMediaData) {
          formData.id = this.digitalMediaData.id;
          this.patchData(formData);
        } else {
          this.postData(formData);
        }
      }
    },
    postData(formData) {
      this.axios
        .post(this.axios.defaults.baseURL + "media/digitalmedias/post/", formData)
        .then((resp) => {
          this.$emit("close", 1);
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },

    patchData(formData) {
      this.axios
        .patch(
          this.axios.defaults.baseURL + "media/digitalmedias/patch/" + formData.id,
          formData
        )
        .then((resp) => {
          this.$emit("close", 1);
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },
    setValues() {
      if (this.digitalMediaData) {
        this.name = this.digitalMediaData.name;
        this.isSubscription = this.digitalMediaData.is_subscription ?? false;
        this.description = this.digitalMediaData.description;
        this.price = this.digitalMediaData.price;
        this.type = this.digitalMediaData.type
        this.bundles = [...(this.digitalMediaData.digital_bundles || [])];
        this.duration = this.digitalMediaData.duration;

        this.tags = this.digitalMediaData.tags

        this.status = this.availStatuses[0]

        if (this.digitalMediaData.status !== undefined) {
          for (let i = 0; i < this.availStatuses.length; i++) {
            if (this.digitalMediaData.status == this.availStatuses[i].key) {
              this.status = this.availStatuses[i]
            }
          }
        }
        else {
          this.status = undefined;
        }
      } else {
        this.name = undefined;
        this.isSubscription = false;
        this.description = undefined;
        this.price = undefined;
        this.type = undefined
        this.bundles = [];

        this.status = undefined;
        this.duration = undefined;

        this.tags = undefined
      }
    },
    fetchTags(searchQ) {
      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "media/tags/?query=" + (searchQ ? searchQ : ''))
          .then((resp) => {
            this.availTags = resp.data.results

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    }

  },

  mounted() { },
};
</script>
